import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Formik, Form } from 'formik';
import useSessionTimeRangeService from '../../api/services/SessionTimeRangeService';
import axiosInstance from '../../api/axiosInstance';
import { endpoints } from '../../api/endPoints';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  timetableId: Yup.string().required('Session Time is required'),
  csrtId: Yup.string().nullable(), // Optional - can be null for break times
  day: Yup.string().required('Day is required'),
  combination: Yup.string().nullable(), // Optional - only used when csrtId is provided
});

const ChangeClassTimetable = ({ open, onClose, onSuccess, classId }) => {
  const { getSubjectsWithTeachersByClassId, unlinkSessionTimeWithSubjectTeacher } = useSessionTimeRangeService();
  
  const [sessionTimes, setSessionTimes] = useState([]);
  const [subjectsTeachers, setSubjectsTeachers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const combinations = ["ALL", "SCIENCE", "PCB", "PCM", "HGE", "EGM", "HGL", "ART", "BUSINESS"];

  useEffect(() => {
    if (open) {
      loadData();
    } else {
      // Reset data when dialog closes
      setSessionTimes([]);
      setSubjectsTeachers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classId]);

  const loadData = async () => {
    setLoadingData(true);
    
    try {
      // Fetch all active session times
      try {
        const activeResponse = await axiosInstance.get(endpoints.sessionTimeRanges.getAllActive);
        
        let sessionTimesList = [];
        if (activeResponse.data && activeResponse.data.data) {
          sessionTimesList = Array.isArray(activeResponse.data.data) 
            ? activeResponse.data.data 
            : [];
        }
        
        // If getAllActive doesn't return data, use the paginated endpoint
        if (sessionTimesList.length === 0) {
          const paginatedUrl = `${endpoints.sessionTimeRanges.getAll}?page=0&size=1000&status=ACTIVE`;
          const paginatedResponse = await axiosInstance.get(paginatedUrl);
          
          if (paginatedResponse.data && paginatedResponse.data.data) {
            const paginatedData = paginatedResponse.data.data;
            if (paginatedData.data && Array.isArray(paginatedData.data)) {
              sessionTimesList = paginatedData.data.map((item) => ({
                id: item.id,
                sessionTime: item.sessionTime,
                timeDescription: item.timeDescription,
                status: item.status,
                createdAt: item.createdAt,
              }));
            }
          }
        } else {
          // Map the data to ensure consistent structure
          sessionTimesList = sessionTimesList.map((item) => ({
            id: item.id,
            sessionTime: item.sessionTime,
            timeDescription: item.timeDescription,
            status: item.status,
            createdAt: item.createdAt,
          }));
        }
        
        setSessionTimes(sessionTimesList);
      } catch (endpointError) {
        console.error('Error fetching session times:', endpointError);
        setSessionTimes([]);
      }

      // Fetch subjects and teachers for this class (only if classId is provided)
      let subjectsList = [];
      
      if (classId) {
        try {
          const subjects = await getSubjectsWithTeachersByClassId(classId);
          subjectsList = Array.isArray(subjects) ? subjects : [];
          setSubjectsTeachers(subjectsList);
        } catch (subjectError) {
          console.error('Error fetching subjects:', subjectError);
          setSubjectsTeachers([]);
        }
      } else {
        setSubjectsTeachers([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status !== 404) {
        toast.error('Error loading session times or subjects. Please try again.');
      }
      setSessionTimes([]);
      setSubjectsTeachers([]);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsSubmitting(true);
    try {
      // Convert special time options and empty string to null for break times
      let csrtId = null;
      if (values.csrtId && 
          values.csrtId !== '' && 
          values.csrtId !== 'BREAK_TIME' && 
          values.csrtId !== 'LUNCH_TIME' && 
          values.csrtId !== 'PRAYER_TIME') {
        csrtId = values.csrtId;
      }
      
      // Combination is optional - use it if provided
      const combination = values.combination || null;
      
      await unlinkSessionTimeWithSubjectTeacher(values.timetableId, csrtId, values.day, combination);
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Unlink session time failed:", error.response?.data || error.message);
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>CHANGE CLASS TIMETABLE</DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        {loadingData ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Formik
            initialValues={{
              timetableId: '',
              csrtId: null, // Allow null for break times
              day: '',
              combination: '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting: formikSubmitting,
              setFieldValue,
            }) => {
              // Determine if combination field should be enabled (only when a teacher-subject is selected)
              const isCombinationEnabled = values.csrtId && 
                                            values.csrtId !== '' && 
                                            values.csrtId !== 'BREAK_TIME' && 
                                            values.csrtId !== 'LUNCH_TIME' && 
                                            values.csrtId !== 'PRAYER_TIME';
              
              return (
              <Form>
                {isSubmitting && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Session Time *</InputLabel>
                      <Select
                        name="timetableId"
                        value={values.timetableId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.timetableId && Boolean(errors.timetableId)}
                        disabled={isSubmitting || formikSubmitting || loadingData}
                      >
                        {!loadingData && sessionTimes.length === 0 ? (
                          <MenuItem disabled>No session times available. Please create session times first.</MenuItem>
                        ) : sessionTimes.length > 0 ? (
                          sessionTimes.map((time) => (
                            <MenuItem key={time.id} value={time.id}>
                              {time.sessionTime || 'N/A'} {time.timeDescription ? `- ${time.timeDescription}` : ''}
                            </MenuItem>
                          ))
                        ) : null}
                      </Select>
                      {touched.timetableId && errors.timetableId && (
                        <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                          {errors.timetableId}
                        </Box>
                      )}
                      {sessionTimes.length === 0 && !loadingData && (
                        <Box sx={{ color: 'warning.main', fontSize: '0.75rem', mt: 0.5 }}>
                          No session times found. Create session times from Session Time Ranges page.
                        </Box>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Subject - Teacher</InputLabel>
                      <Select
                        name="csrtId"
                        value={values.csrtId || ''}
                        onChange={(event) => {
                          const newValue = event.target.value;
                          handleChange(event);
                          // Clear combination when break time is selected
                          if (newValue === 'BREAK_TIME' || newValue === 'LUNCH_TIME' || newValue === 'PRAYER_TIME' || !newValue) {
                            setFieldValue('combination', '', false);
                          }
                        }}
                        onBlur={handleBlur}
                        error={touched.csrtId && Boolean(errors.csrtId)}
                        disabled={isSubmitting || formikSubmitting || loadingData}
                        displayEmpty
                      >
                        <MenuItem value="BREAK_TIME">
                          Break Time (No Subject-Teacher)
                        </MenuItem>
                        
                        {subjectsTeachers.length === 0 ? (
                          <MenuItem disabled>No subjects-teachers available for this class.</MenuItem>
                        ) : (
                          subjectsTeachers.map((item) => (
                            <MenuItem key={item.csrtId} value={item.csrtId}>
                              {item.subjectName || 'N/A'} - {item.teacherName || 'No Teacher'}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {touched.csrtId && errors.csrtId && (
                        <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                          {errors.csrtId}
                        </Box>
                      )}
                      <Box sx={{ color: 'info.main', fontSize: '0.75rem', mt: 0.5 }}>
                        Select "Break Time" to unlink break time (no subject-teacher assigned).
                      </Box>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Day *</InputLabel>
                      <Select
                        name="day"
                        value={values.day}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.day && Boolean(errors.day)}
                        disabled={isSubmitting || formikSubmitting}
                      >
                        {days.map((day) => (
                          <MenuItem key={day} value={day}>
                            {day}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.day && errors.day && (
                        <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                          {errors.day}
                        </Box>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Combination</InputLabel>
                      <Select
                        name="combination"
                        value={values.combination || ''}
                        onChange={(event) => {
                          setFieldValue('combination', event.target.value, true);
                        }}
                        onBlur={handleBlur}
                        error={touched.combination && Boolean(errors.combination)}
                        disabled={isSubmitting || formikSubmitting || !isCombinationEnabled}
                      >
                        <MenuItem value="">
                          <em>Select a combination (optional)</em>
                        </MenuItem>
                        {combinations.map((combo) => (
                          <MenuItem key={combo} value={combo}>
                            {combo}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.combination && errors.combination && (
                        <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                          {errors.combination}
                        </Box>
                      )}
                      {!isCombinationEnabled && (
                        <Box sx={{ color: 'info.main', fontSize: '0.75rem', mt: 0.5 }}>
                          Combination is available when unassigning a teacher-subject
                        </Box>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>

                <DialogActions sx={{ mt: 2 }}>
                  <Button onClick={onClose} disabled={isSubmitting || formikSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={isSubmitting || formikSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                  >
                    {isSubmitting ? 'Unlinking...' : 'Unlink'}
                  </Button>
                </DialogActions>
              </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChangeClassTimetable;

